import { Component, HostBinding, Input, inject } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { WINDOW } from '@shared/dom';
import { DialogConfig, DialogIcon } from '@shared/components/dialog';
import { I18nService } from '@shared/i18n/i18n.service';
import { DialogService } from '@shared/services/dialog';
import { deleteAccount, deleteCurrentRegistration, deleteDeviceId, deleteLogin, StoreService } from '@shared/store';

@Component({
  selector: 'app-layout-header',
  templateUrl: './layout-header.component.html',
  styleUrls: ['./layout-header.component.scss'],
  standalone: false,
})
export class LayoutHeaderComponent {
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly dialog = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(StoreService);
  private readonly window = inject<Window>(WINDOW);

  @HostBinding('class.layout-header') readonly binding = true;

  /**
   * Whether to show the back button on the header.
   */
  @Input() showBackButton = false;

  /**
   * Whether to show the switch user button on the header. Intended for authentication landing pages.
   */
  @Input() showSwitchUser = false;

  /**
   * An onBeforeGoBack function resolves to a boolean: true if the goBack should proceed, false to abort it.
   */
  @Input() onBeforeGoBack: (() => Promise<boolean>) | undefined;

  /**
   * A URL to go back to. The button will route to here if provided, otherwise it will use browser back.
   */
  @Input() goBackTo: string | undefined;

  public async goBackClicked(): Promise<void> {
    if (this.onBeforeGoBack) {
      const proceed = await this.onBeforeGoBack();
      if (!proceed) {
        return;
      }
    }

    if (this.goBackTo) {
      this.router.navigate([this.goBackTo]);
    } else {
      this.location.back();
    }
  }

  public switchUserClicked(): void {
    const dialogData: DialogConfig = {
      icon: DialogIcon.AlertRed,
      title: this.i18n.getPhrase('topmenu.switch_user.dialog.title'),
      content: this.i18n.getPhrase('topmenu.switch_user.dialog.content'),
      secondary: this.i18n.getPhrase('topmenu.switch_user.dialog.cancel'),
      primary: this.i18n.getPhrase('topmenu.switch_user.dialog.confirm'),
    };

    this.dialog.openConfirmDialog({ data: dialogData }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.store.dispatch(deleteAccount());
        this.store.dispatch(deleteDeviceId());
        this.store.dispatch(deleteLogin());
        this.store.dispatch(deleteCurrentRegistration());
        this.window.location.reload();
      }
    });
  }
}
